import React, { useCallback, useEffect, useState } from 'react';
import {
    Table, Button, Space, Tag, Modal, message, Card, Popconfirm, Tooltip, InputNumber, Tabs,
    Typography, Select, Switch, Checkbox, Statistic, Row, Col, Alert,
} from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { adminPath } from '../../../shared/config/paths';
import evmAdminService from '../../../shared/services/evmAdminService';
import { formatMoney, currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Text } = Typography;

const STATUS = {
    active: ['生效', 'success'],
    suspended: ['已暂停', 'warning'],
    terminated: ['已终止', 'default'],
    cancelled: ['已取消', 'default'],
};
const INV = { paid: 'success', unpaid: 'warning', cancelled: 'default', void: 'default' };
const CYCLES = [1, 3, 6, 12, 24, 36];
const ok = (res) => res && res.status === 'success';
const daysLeft = (v) => (v ? Math.ceil((new Date(String(v).replace(' ', 'T')) - Date.now()) / 86400000) : null);

const SubscriptionManagement = () => {
    const [rows, setRows] = useState([]);
    const [enabled, setEnabled] = useState(true);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');
    const [renewing, setRenewing] = useState(null); // {row, months, pay}
    const [extending, setExtending] = useState(null); // {row, days}

    const load = useCallback(async () => {
        setLoading(true);
        const [a, b] = await Promise.all([
            evmAdminService.getSubscriptions(status ? { status } : {}),
            evmAdminService.getRenewalReport(),
        ]);
        setLoading(false);
        if (ok(a)) {
            setRows(a.data.items || []);
            setEnabled(!!a.data.enabled);
        } else message.error(a?.msg || a?.error || '加载失败');
        if (ok(b)) setReport(b.data);
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const act = async (p, okMsg = '已完成') => {
        const res = await p;
        if (ok(res)) {
            message.success(res.msg || okMsg);
            load();
        } else message.error(res?.msg || res?.error || '操作失败');
    };

    const columns = [
        {
            title: '云服务器', key: 'vm',
            render: (_, r) => (r.vm?.name
                ? <Space size={6}><Link to={adminPath(`/evm/vms/${r.vm.id}`)}>{r.vm.name}</Link><Text type="secondary">#{r.vm.id}</Text></Space>
                : <Text type="secondary">#{r.vm?.id || '-'}</Text>),
        },
        { title: '用户', key: 'user', render: (_, r) => r.user?.username || `#${r.user?.id}` },
        { title: '周期', dataIndex: 'cycle_months', render: (m) => `${m} 个月` },
        { title: '续费价', dataIndex: 'price', render: (v, r) => (v != null ? formatMoney(v, r.currency || PLATFORM_CURRENCY) : '-') },
        {
            title: '到期', dataIndex: 'expiry_date',
            render: (v) => {
                const d = daysLeft(v);
                return v ? <Text type={d <= 7 ? 'danger' : undefined}>{String(v).slice(0, 16)}</Text> : '-';
            },
        },
        {
            title: '自动续费', dataIndex: 'auto_renew',
            render: (v, r) => (
                <Switch size="small" checked={v} disabled={!['active', 'suspended'].includes(r.status)}
                    onChange={(x) => act(evmAdminService.setSubscriptionAutoRenew(r.id, x))} />
            ),
        },
        {
            title: '状态', dataIndex: 'status',
            render: (s, r) => {
                const [label, color] = STATUS[s] || [s, 'default'];
                return <Tag color={color}>{label}{r.suspended_by_billing ? ' · 欠费' : ''}</Tag>;
            },
        },
        {
            title: '最近续费账单', key: 'inv',
            render: (_, r) => (r.last_invoice
                ? <Space size={4}><Tag color={INV[r.last_invoice.status]}>{r.last_invoice.status}</Tag><Text>{formatMoney(r.last_invoice.amount, r.last_invoice.currency || PLATFORM_CURRENCY)}</Text></Space>
                : '-'),
        },
        {
            title: '操作', key: 'op', fixed: 'right',
            render: (_, r) => {
                const live = ['active', 'suspended'].includes(r.status);
                return (
                    <Space size={4}>
                        <Button size="small" type="link" disabled={!live}
                            onClick={() => setRenewing({ row: r, months: r.cycle_months, pay: false })}>续费</Button>
                        <Button size="small" type="link" disabled={!live}
                            onClick={() => setExtending({ row: r, days: 7 })}>延期</Button>
                        {r.status === 'active' && (
                            <Popconfirm title="暂停该云服务器？" onConfirm={() => act(evmAdminService.suspendSubscription(r.id))}>
                                <Button size="small" type="link" danger>暂停</Button>
                            </Popconfirm>
                        )}
                        {r.status === 'suspended' && (
                            <Popconfirm title="恢复该云服务器？" onConfirm={() => act(evmAdminService.unsuspendSubscription(r.id))}>
                                <Button size="small" type="link">恢复</Button>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    const table = (data, extra = {}) => (
        <Table rowKey="id" size="small" columns={columns} dataSource={data} loading={loading}
            scroll={{ x: 'max-content' }} pagination={data.length > 20 ? { pageSize: 20 } : false} {...extra} />
    );

    const gates = report?.gates;
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Typography.Title level={4} style={{ margin: 0 }}>云服务器订阅</Typography.Title>
                <Button icon={<SyncOutlined />} onClick={load} loading={loading} />
            </div>
            {!enabled && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="订阅未启用（缺少 evm-vm 承载产品，需执行迁移）" />}
            {gates && (
                <Space size={8} wrap style={{ marginBottom: 12 }}>
                    <Tooltip title="BILLING_AUTOPAY_ENABLED"><Tag color={gates.autopay ? 'success' : 'default'}>自动扣款 {gates.autopay ? '开' : '关'}</Tag></Tooltip>
                    <Tooltip title="BILLING_SUSPEND_EXPIRED_ENABLED（关=只记日志）"><Tag color={gates.suspend_expired ? 'error' : 'default'}>到期暂停/终止 {gates.suspend_expired ? '开' : '关'}</Tag></Tooltip>
                    <Tag>宽限 {gates.grace_hours} 小时</Tag>
                </Space>
            )}
            <Card styles={{ body: { paddingTop: 4 } }}>
                <Tabs items={[
                    {
                        key: 'all', label: '订阅',
                        children: (
                            <>
                                <Select value={status} onChange={setStatus} style={{ width: 140, marginBottom: 12 }}
                                    options={[{ value: '', label: '全部状态' }, ...Object.entries(STATUS).map(([v, [l]]) => ({ value: v, label: l }))]} />
                                {table(rows)}
                            </>
                        ),
                    },
                    {
                        key: 'report', label: '续费报表',
                        children: report && (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Row gutter={16}>
                                    <Col xs={12} md={6}><Statistic title="30 天内到期" value={report.upcoming.length} /></Col>
                                    <Col xs={12} md={6}><Statistic title="预计自动续费" prefix={currencySymbol(report.currency || PLATFORM_CURRENCY)} value={report.upcoming_total} /></Col>
                                    <Col xs={12} md={6}><Statistic title="扣款失败" value={report.failed.length} valueStyle={report.failed.length ? { color: '#cf1322' } : undefined} /></Col>
                                    <Col xs={12} md={6}><Statistic title="已暂停" value={report.suspended.length} /></Col>
                                </Row>
                                <div><Text strong>30 天内到期</Text>{table(report.upcoming)}</div>
                                <div><Text strong>扣款失败（未付续费账单）</Text>{table(report.failed)}</div>
                                <div><Text strong>已暂停</Text>{table(report.suspended)}</div>
                            </Space>
                        ),
                    },
                ]} />
            </Card>

            <Modal title="续费" open={!!renewing} onCancel={() => setRenewing(null)} destroyOnClose
                onOk={async () => {
                    await act(evmAdminService.renewSubscription(renewing.row.id, renewing.months, renewing.pay));
                    setRenewing(null);
                }}>
                {renewing && (
                    <Space direction="vertical">
                        <Select value={renewing.months} style={{ width: 160 }}
                            onChange={(m) => setRenewing({ ...renewing, months: m })}
                            options={CYCLES.map((m) => ({ value: m, label: `${m} 个月` }))} />
                        <Checkbox checked={renewing.pay} onChange={(e) => setRenewing({ ...renewing, pay: e.target.checked })}>
                            从用户余额扣款
                        </Checkbox>
                    </Space>
                )}
            </Modal>

            <Modal title="免费延期" open={!!extending} onCancel={() => setExtending(null)} destroyOnClose
                onOk={async () => {
                    await act(evmAdminService.extendSubscription(extending.row.id, extending.days));
                    setExtending(null);
                }}>
                {extending && (
                    <InputNumber min={1} max={3660} value={extending.days} addonAfter="天"
                        onChange={(d) => setExtending({ ...extending, days: d })} />
                )}
            </Modal>
        </div>
    );
};

export default SubscriptionManagement;
